import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { UserPlus, ShieldCheck, Send, Sparkles, Heart, Zap, Users, Globe } from "lucide-react";
import BrandMarquee from "@/components/BrandMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import ParallaxImage from "@/components/ParallaxImage";
import { CREATOR_FAQS, IMAGES } from "@/data/content";
import { AUTH_LINKS } from "@/data/auth-links";

const PERKS = [
  { Icon: Heart, t: "Access Brand Collaborations", d: "Get discovered by brands looking for creators across niches, industries and regions." },
  { Icon: Zap, t: "Campaign Opportunities", d: "Participate in influencer campaigns and UGC collaborations matched to your content style." },
  { Icon: Sparkles, t: "Work with Relevant Brands", d: "Collaborate with brands that align with your audience, content and niche — no random pitches." },
  { Icon: Users, t: "Grow as a Creator", d: "Build your portfolio through meaningful brand collaborations and ongoing campaigns." },
  { Icon: ShieldCheck, t: "Transparent Process", d: "Clear communication, defined deliverables and structured campaign workflows." },
  { Icon: Globe, t: "Regional & Global Reach", d: "From hyperlocal Tamil, Telugu, Malayalam, Kannada creators to pan-India opportunities." },
];

const STEPS = [
  { n: "01", t: "Sign Up", d: "Create your profile and share your social media details." },
  { n: "02", t: "Get Verified", d: "Our team reviews your profile and ensures quality and authenticity." },
  { n: "03", t: "Get Opportunities", d: "Start receiving collaboration opportunities from relevant brands." },
  { n: "04", t: "Create & Collaborate", d: "Work with brands, create content and grow your portfolio." },
];

const WHO = [
  "Influencers & content creators",
  "UGC creators",
  "Micro, macro & niche creators",
  "Regional / language-based creators",
  "Lifestyle, fashion, tech, food, travel & more",
];

export default function Creators() {
  return (
    <div data-testid="creators-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden grain-bg">
        <div className="absolute top-20 left-1/3 w-[500px] h-[500px] bg-[#CCFF00] opacity-[0.06] rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#FF4A00] opacity-[0.08] rounded-full blur-[160px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-7"
            >
              <SectionLabel className="mb-6">For Creators</SectionLabel>
              <h1
                data-testid="creators-hero-title"
                className="font-display text-4xl md:text-6xl lg:text-[6rem] font-semibold tracking-tighter leading-[0.92]"
              >
                Turn your content into <span className="italic text-[#FF4A00] font-medium">brand opportunities.</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl text-[#A3A3A3] max-w-xl leading-relaxed">
                Join Incer and collaborate with brands that align with your content and audience. Discover
                campaigns, create impactful content and grow as a creator.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <Link
                  to={AUTH_LINKS.creatorSignUp}
                  data-testid="creators-cta-signup"
                  className="inline-flex items-center gap-2 bg-[#FF4A00] text-white text-base font-medium px-7 py-4 rounded-full hover:bg-[#E64300] transition-all active:scale-95"
                >
                  Sign up as a Creator <UserPlus className="w-5 h-5" />
                </Link>
                <span className="text-sm text-[#A3A3A3] w-full sm:w-auto">Free to join · Reviewed by our team</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-square rounded-3xl overflow-hidden border border-[#1a1a1a]">
                <ParallaxImage
                  src={IMAGES.creatorHero}
                  alt="Content creator taking a selfie with ring light and social media icons"
                  className="absolute inset-0"
                  intensity={0.15}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent z-10" />
                <div className="absolute top-6 left-6 px-3 py-1.5 rounded-full bg-[#050505]/80 backdrop-blur-sm border border-[#222] text-xs font-medium uppercase tracking-widest z-20">
                  <span className="text-[#FF4A00]">●</span> Live · Discover
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRANDS WE WORK WITH */}
      <section className="py-16 border-y border-[#1a1a1a] bg-[#070707]" data-testid="creators-brands">
        <div className="text-center mb-8">
          <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
            Work with leading brands across industries
          </h3>
        </div>
        <BrandMarquee speed={28} />
      </section>

      {/* WHY JOIN */}
      <section className="py-24 md:py-32" data-testid="creators-perks">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">Why join Incer</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              A creator-first platform built for real opportunities.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERKS.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`creator-perk-${i}`}
                className="card-glow group p-8 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF4A00]/20 transition-colors">
                  <p.Icon className="w-5 h-5 text-[#FF4A00]" />
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight mb-2">{p.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO JOIN — TIMELINE */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="creators-steps">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">How to join</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Four steps to your first collaboration.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`creator-step-${i}`}
                className="relative p-8 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="font-display text-5xl font-semibold text-[#FF4A00]/30 mb-6">{s.n}</div>
                <h3 className="font-display text-2xl font-medium tracking-tight mb-3">{s.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed text-sm">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO CAN JOIN */}
      <section className="py-24 md:py-32" data-testid="creators-who">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6">
              <SectionLabel className="mb-5">Who can join</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                If you create, you belong here.
              </h2>
              <p className="mt-6 text-lg text-[#A3A3A3] max-w-md">
                Incer is open to creators across categories. If you create content and have an engaged
                audience, you can join.
              </p>
            </div>
            <div className="lg:col-span-6">
              <ul className="space-y-2">
                {WHO.map((w, i) => (
                  <li
                    key={i}
                    data-testid={`creator-who-${i}`}
                    className="group flex items-center justify-between p-6 border border-[#1a1a1a] rounded-2xl hover:border-[#FF4A00] hover:bg-[#FF4A00]/5 transition-all"
                  >
                    <span className="font-display text-xl md:text-2xl font-medium tracking-tight">{w}</span>
                    <Send className="w-5 h-5 text-[#A3A3A3] group-hover:text-[#FF4A00] group-hover:translate-x-1 transition-all" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        testId="creator-cta"
        eyebrow="Ready when you are"
        title="Start collaborating with brands that match your voice."
        description="Apply to join Incer — it's free, and our team reviews every profile personally."
        ctaText="Apply now"
        ctaTo="/contact"
      />
      <FAQSection items={CREATOR_FAQS} testId="creator-faq" />
    </div>
  );
}
